import type { Category, ContentTopic } from "api/config/chalice-types";
import { Dispatch, SetStateAction, useState } from "react";

// --- Define and Export All Necessary Types --- //

// Topic Modals
export type TopicModalData = {
  type: "new-topic" | "edit-topic";
  categoryName: string | null;
  topic?: ContentTopic & { index?: number };
};

export type GeneratePostsModalData = {
  type: "generate-posts";
  topic: ContentTopic & { index: number };
};

export type ImportTopicsModalData = {
  type: "import-topics";
};

// Combined state for topic-related modals
export type CombinedTopicModalState =
  | TopicModalData
  | GeneratePostsModalData
  | ImportTopicsModalData;

// Category Modals
export type CategoryModalData = {
  type: "new-category" | "edit-category";
  currentName?: string;
  initialColor?: string | null; // Ensure this is included and the type is exported
  categoryId?: string;
};

export type CategoryDeleteConfirmData = {
  category: Category;
  topicCount: number;
};
// --- End Type Definitions --- //

// --- Define return type for the hook --- //
interface UseTopicsPageModalsReturn {
  showModal: CombinedTopicModalState | null; // Use combined type
  showCategoryModal: CategoryModalData | null; // Use the exported type
  categoryName: string;
  setCategoryName: Dispatch<SetStateAction<string>>;
  categoryToDeleteConfirm: CategoryDeleteConfirmData | null;
  openNewTopicModal: (categoryId: string | null) => void;
  openEditTopicModal: (
    topicWithIndex: ContentTopic & { index: number }
  ) => void;
  openGeneratePostsModal: (
    topicWithIndex: ContentTopic & { index: number }
  ) => void;
  openImportTopicsModal: () => void;
  openNewCategoryModal: () => void;
  openEditCategoryModal: (category: Category, color?: string | null) => void;
  openCategoryDeleteConfirmModal: (
    category: Category,
    topicCount: number
  ) => void;
  closeModal: () => void;
  closeCategoryModal: () => void;
  closeCategoryDeleteConfirmModal: () => void;
}
// --- End Return Type Definition --- //

export const useTopicsPageModals = (): UseTopicsPageModalsReturn => {
  // Use the correct state types
  const [showModal, setShowModal] = useState<CombinedTopicModalState | null>(
    null
  );
  const [showCategoryModal, setShowCategoryModal] =
    useState<CategoryModalData | null>(null);
  const [categoryToDeleteConfirm, setCategoryToDeleteConfirm] =
    useState<CategoryDeleteConfirmData | null>(null);
  const [categoryName, setCategoryName] = useState("");

  // --- Modal Opener Functions --- //
  const openNewTopicModal = (categoryName: string | null = null) => {
    setShowModal({ type: "new-topic", categoryName });
  };

  const openEditTopicModal = (
    topicWithIndex: ContentTopic & { index?: number }
  ) => {
    setShowModal({
      type: "edit-topic",
      categoryName: topicWithIndex.category_id ?? null,
      topic: topicWithIndex,
    });
  };

  const openGeneratePostsModal = (
    topicWithIndex: ContentTopic & { index: number }
  ) => {
    setShowModal({ type: "generate-posts", topic: topicWithIndex });
  };

  const openImportTopicsModal = () => {
    setShowModal({ type: "import-topics" });
  };

  const openNewCategoryModal = () => {
    setShowCategoryModal({ type: "new-category" });
    setCategoryName("");
  };

  const openEditCategoryModal = (category: Category, color?: string | null) => {
    setCategoryName(category.name);
    // Correctly uses the unified CategoryModalData type
    setShowCategoryModal({
      type: "edit-category",
      currentName: category.name,
      initialColor: color,
      categoryId: category.id,
    });
  };

  const openCategoryDeleteConfirmModal = (
    category: Category,
    topicCount: number
  ) => {
    setCategoryToDeleteConfirm({ category, topicCount });
  };

  // --- Modal Closer Functions --- //
  const closeModal = () => {
    setShowModal(null);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(null);
    setCategoryName("");
  };

  const closeCategoryDeleteConfirmModal = () => {
    setCategoryToDeleteConfirm(null);
  };

  // --- Return Hook Values --- //
  return {
    showModal,
    showCategoryModal,
    categoryName,
    setCategoryName,
    categoryToDeleteConfirm,
    openNewTopicModal,
    openEditTopicModal,
    openGeneratePostsModal,
    openImportTopicsModal,
    openNewCategoryModal,
    openEditCategoryModal,
    openCategoryDeleteConfirmModal,
    closeModal,
    closeCategoryModal,
    closeCategoryDeleteConfirmModal,
  };
};
